import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { Panel } from '../common/Panel';
import { TableSkeleton } from '../common/Skeleton';

type BankAccountItem = {
  id: string;
  accountNumber: string;
  iban: string | null;
  friendlyName: string | null;
  memberId: string | null;
  memberDisplayName: string | null;
  bankSource: string;
  isActive: boolean;
};

type EditDialogProps = {
  open: boolean;
  account: BankAccountItem | null;
  onClose: () => void;
  onSaved: () => void;
};

function EditDialog({ open, account, onClose, onSaved }: EditDialogProps) {
  const membersQuery = trpc.household.get.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.bankAccount.update.useMutation({
    onSuccess: async () => {
      await utils.bankAccount.list.invalidate();
      toast.success('Bankkonto uppdaterat');
      onSaved();
    },
    onError: (err) => toast.error(err.message),
  });

  const [friendlyName, setFriendlyName] = useState('');
  const [iban, setIban] = useState('');
  const [memberId, setMemberId] = useState('none');

  const members: { id: string; displayName: string }[] =
    (membersQuery.data as { members?: { id: string; displayName: string }[] } | undefined)
      ?.members ?? [];

  // Reset form when dialog opens with a new account
  const [lastAccountId, setLastAccountId] = useState<string | null>(null);
  if (open && account && account.id !== lastAccountId) {
    setLastAccountId(account.id);
    setFriendlyName(account.friendlyName ?? '');
    setIban(account.iban ?? '');
    setMemberId(account.memberId ?? 'none');
  }
  if (!open && lastAccountId !== null) {
    setLastAccountId(null);
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!account) return;
    updateMutation.mutate({
      id: account.id,
      friendlyName: friendlyName.trim() || null,
      iban: iban.trim() || null,
      memberId: memberId === 'none' ? null : memberId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redigera bankkonto</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="bank-account-number">Kontonummer</Label>
            <Input
              id="bank-account-number"
              value={account?.accountNumber ?? ''}
              disabled
              className="bg-muted"
            />
          </div>

          <div>
            <Label htmlFor="friendlyName">Namn</Label>
            <Input
              id="friendlyName"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              placeholder="T.ex. Lönekonto"
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="iban">IBAN</Label>
            <Input
              id="iban"
              value={iban}
              onChange={(e) => setIban(e.target.value.toUpperCase())}
              placeholder="SE00 0000 0000 0000 0000 0000"
              maxLength={34}
            />
          </div>

          <div>
            <Label htmlFor="bank-account-member">Medlem</Label>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger id="bank-account-member" className="w-full">
                <SelectValue placeholder="Ingen tilldelad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ingen tilldelad</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Sparar…' : 'Spara'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BankAccountList() {
  const { confirm } = useConfirm();
  const query = trpc.bankAccount.list.useQuery();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.bankAccount.delete.useMutation({
    onSuccess: () => {
      void utils.bankAccount.list.invalidate();
      toast.success('Bankkonto borttaget');
    },
    onError: (err) => toast.error(err.message),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountItem | null>(null);

  if (query.isLoading) return <TableSkeleton rows={3} columns={3} />;
  if (query.error) return <p className="text-sm text-destructive">{query.error.message}</p>;

  const accounts: BankAccountItem[] = (query.data ?? []) as BankAccountItem[];

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon="🏦"
        title="Inga bankkonton"
        description="Bankkonton skapas automatiskt vid import av transaktioner."
      />
    );
  }

  return (
    <>
      <Panel title="Bankkonton" description="Ge kontona egna namn så känns de igen i listorna.">
        <div className="-mt-1 -mb-3 divide-y divide-border">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="break-words font-medium text-sm">
                  {account.friendlyName ?? account.accountNumber}
                </div>
                <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                  <span>{account.accountNumber}</span>
                  {account.iban && (
                    <span className="break-all text-muted-foreground/70">IBAN: {account.iban}</span>
                  )}
                  {account.memberDisplayName && (
                    <>
                      <span>·</span>
                      <span>{account.memberDisplayName}</span>
                    </>
                  )}
                  <span>·</span>
                  <span className="capitalize">{account.bankSource}</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={`Redigera ${account.friendlyName ?? account.accountNumber}`}
                  onClick={() => {
                    setEditingAccount(account);
                    setDialogOpen(true);
                  }}
                >
                  Redigera
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={deleteMutation.isPending}
                  aria-label={`Ta bort ${account.friendlyName ?? account.accountNumber}`}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Ta bort bankkonto',
                      message: `Vill du ta bort "${account.friendlyName ?? account.accountNumber}"? Kontot avaktiveras men transaktionerna behålls.`,
                      confirmLabel: 'Ta bort',
                      destructive: true,
                    });
                    if (ok) deleteMutation.mutate({ id: account.id });
                  }}
                >
                  Ta bort
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <EditDialog
        open={dialogOpen}
        account={editingAccount}
        onClose={() => {
          setDialogOpen(false);
          setEditingAccount(null);
        }}
        onSaved={() => {
          setDialogOpen(false);
          setEditingAccount(null);
        }}
      />
    </>
  );
}
